import { useCallback, useEffect, useMemo, useState } from 'react'
import { getMastodonAccountId, type MastodonSession } from '@/lib/mastodon'
import {
    FOLLOWING_TAB_ID,
    mastodonTabPrefsKey,
    parseMastodonTabPrefs,
    reconcileMastodonTabs,
    writeMastodonTabPrefs,
    type MastodonTabsPreference,
} from '@/lib/mastodonTabPrefs'
import {
    normalizeMastodonTagName,
    mastodonTagDisplayName,
    MASTODON_HASHTAG_URI_PREFIX,
    type MastodonFollowedTag,
} from '@/lib/mastodonTag'
import type { TabEntry } from '@/services/userPreferencesService'
import type { ResolvedFeed } from '@/types/feeds'
import type { MastodonList } from '@/types/lists'

const FOLLOWING_FEED: ResolvedFeed = { uri: FOLLOWING_TAB_ID, displayName: 'Following' }

/** Static Mastodon tabs that exist regardless of lists authority. */
export const MASTODON_BASE_FEEDS: ResolvedFeed[] = [
    FOLLOWING_FEED,
    { uri: 'mastodon:local', displayName: 'Local' },
    { uri: 'mastodon:federated', displayName: 'Federated' },
]

const BASE_FEED_IDS = new Set(MASTODON_BASE_FEEDS.map(f => f.uri))
const EMPTY_PREFS: MastodonTabsPreference = { version: 1, tabs: [] }

export interface MastodonTabEntry {
    id: string
    label: string
    visible: boolean
    position: number
    isLocked?: boolean
    feed?: ResolvedFeed
}

export interface UseMastodonTabPrefsOptions {
    lists: MastodonList[]
    isListsAuthoritative: boolean
    followedTags: MastodonFollowedTag[]
    isFollowedTagsAuthoritative: boolean
    session: MastodonSession | null
}

export interface UseMastodonTabPrefsResult {
    /** Reconciled entries with display labels, in stored order. */
    entries: MastodonTabEntry[]
    /** Visible feeds only, in order — what Home renders as tabs. */
    visibleFeeds: ResolvedFeed[]
    /** All renderable feeds (visible + hidden), for the management page. */
    allFeeds: ResolvedFeed[]
    /** True only when BOTH lists and followed tags are authoritative — see module doc. */
    isLiveAuthoritative: boolean
    /** Save a full reordered/visibility-updated tab list. */
    saveTabs: (tabs: MastodonTabEntry[]) => boolean
    /** Low-level transactional mutator: re-reads current stored state, applies updater, single write. */
    mutatePreference: (
        updater: (current: MastodonTabsPreference) => MastodonTabsPreference
    ) => boolean
}

export function useMastodonTabPrefs({
    lists,
    isListsAuthoritative,
    followedTags,
    isFollowedTagsAuthoritative,
    session,
}: UseMastodonTabPrefsOptions): UseMastodonTabPrefsResult {
    // Combined authority: a mutation re-reads stored prefs and reconciles against
    // liveFeeds — if either source (lists, followed tags) is missing from liveFeeds,
    // that write silently prunes the other source's stored entries. So mutations, the
    // full reconcile, and Home's readiness gate all wait for BOTH sources.
    const isLiveAuthoritative = isListsAuthoritative && isFollowedTagsAuthoritative
    const scope = session ? getMastodonAccountId(session) : null
    const storageKey = scope ? mastodonTabPrefsKey(scope) : null

    const readStored = useCallback((): MastodonTabsPreference => {
        if (!storageKey) return EMPTY_PREFS
        try {
            return parseMastodonTabPrefs(localStorage.getItem(storageKey)) ?? EMPTY_PREFS
        } catch {
            return EMPTY_PREFS
        }
    }, [storageKey])

    const [stored, setStored] = useState<MastodonTabsPreference>(() => readStored())

    // Re-read when the scope changes (account switch) so a stale account's prefs never
    // linger under the new scope.
    useEffect(() => {
        setStored(readStored())
    }, [storageKey, readStored])

    // Same-tab sync: the Feeds page and Home both read/write this key. Native storage
    // events only fire cross-tab, so mutatePreference dispatches a synthetic one. Always
    // re-read from event.newValue rather than captured state, so multiple hook instances
    // observing events in different orders can't clobber each other.
    useEffect(() => {
        if (!storageKey) return
        const handleStorage = (e: StorageEvent) => {
            if (e.key !== storageKey) return
            setStored(parseMastodonTabPrefs(e.newValue) ?? EMPTY_PREFS)
        }
        window.addEventListener('storage', handleStorage)
        return () => window.removeEventListener('storage', handleStorage)
    }, [storageKey])

    const liveFeeds = useMemo<ResolvedFeed[]>(() => {
        if (!isLiveAuthoritative) return MASTODON_BASE_FEEDS
        const tagFeeds: ResolvedFeed[] = []
        for (const tag of followedTags) {
            const normalized = normalizeMastodonTagName(tag.name)
            if (!normalized) continue
            tagFeeds.push({
                uri: MASTODON_HASHTAG_URI_PREFIX + normalized,
                displayName: mastodonTagDisplayName(tag.name),
            })
        }
        return [
            ...MASTODON_BASE_FEEDS,
            ...lists.map(l => ({
                uri: `mastodon-list:${l.id}`,
                displayName: l.title,
                type: 'list' as const,
            })),
            ...tagFeeds,
        ]
    }, [isLiveAuthoritative, lists, followedTags])

    // Non-authoritative: only reconcile against base feeds, so stored list/tag entries are
    // never touched (pruned or otherwise) in memory. Authoritative: full reconcile against
    // the complete live set. Neither branch writes to storage — this is a read-only
    // projection.
    const reconciledTabs = useMemo<TabEntry[]>(() => {
        if (!isLiveAuthoritative) {
            const baseOnly = stored.tabs.filter(
                t => t.id === FOLLOWING_TAB_ID || BASE_FEED_IDS.has(t.id)
            )
            return reconcileMastodonTabs(baseOnly, MASTODON_BASE_FEEDS)
        }
        return reconcileMastodonTabs(stored.tabs, liveFeeds)
    }, [isLiveAuthoritative, stored.tabs, liveFeeds])

    const feedMap = useMemo(() => new Map(liveFeeds.map(f => [f.uri, f])), [liveFeeds])

    const entries = useMemo<MastodonTabEntry[]>(
        () =>
            reconciledTabs
                .slice()
                .sort((a, b) => a.position - b.position)
                .map(t => {
                    if (t.id === FOLLOWING_TAB_ID) {
                        return {
                            id: t.id,
                            label: FOLLOWING_FEED.displayName,
                            visible: true,
                            position: t.position,
                            isLocked: true,
                            feed: FOLLOWING_FEED,
                        }
                    }
                    const feed = feedMap.get(t.id)
                    return {
                        id: t.id,
                        label: feed?.displayName ?? t.id,
                        visible: t.visible,
                        position: t.position,
                        feed,
                    }
                }),
        [reconciledTabs, feedMap]
    )

    const toFeed = (e: MastodonTabEntry): ResolvedFeed =>
        e.feed ?? { uri: e.id, displayName: e.label }
    const visibleFeeds = useMemo(() => entries.filter(e => e.visible).map(toFeed), [entries])
    const allFeeds = useMemo(() => entries.map(toFeed), [entries])

    const mutatePreference = useCallback(
        (updater: (current: MastodonTabsPreference) => MastodonTabsPreference): boolean => {
            if (!storageKey) return false
            try {
                const current = readStored()
                const next = updater(current)
                writeMastodonTabPrefs(storageKey, next)
                setStored(next)
                return true
            } catch {
                // Write failed (quota, privacy mode) — re-derive from the current stored
                // record rather than restoring a pre-click snapshot, which could clobber a
                // newer concurrent update.
                setStored(readStored())
                return false
            }
        },
        [storageKey, readStored]
    )

    const saveTabs = useCallback(
        (tabs: MastodonTabEntry[]): boolean => {
            const tabEntries: TabEntry[] = tabs.map((t, i) => ({
                id: t.id,
                visible: t.id === FOLLOWING_TAB_ID ? true : t.visible,
                position: i,
            }))
            return mutatePreference(() => ({
                version: 1,
                tabs: tabEntries,
            }))
        },
        [mutatePreference]
    )

    return {
        entries,
        visibleFeeds,
        allFeeds,
        isLiveAuthoritative,
        saveTabs,
        mutatePreference,
    }
}
